import { BarChart3, FileText, PiggyBank, ReceiptText } from "lucide-react";
import { useAuth } from "@/App";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { EmployeeProfileHeader } from "@/pages/users/components/EmployeeProfileHeader";
import { EmployeeProfileKpis } from "@/pages/users/components/EmployeeProfileKpis";
import {
  EmployeeRecentActivity,
  buildRecentEvents,
} from "@/pages/users/components/EmployeeRecentActivity";
import { useEmployeeActivityDetail } from "@/hooks/queries/useEmployeeActivityDetail";

// /api/reporting/employees/:agentId/detail is gated to consultant/manager/
// admin/super_admin (requireReadAccess) — "agent" and "dev" are not in that
// list, so the self-activity summary below can only be fetched for these
// roles. See the implementation report for the "agent can't see their own
// stats" gap this leaves open.
const ROLES_WITH_SELF_ACTIVITY = ["consultant", "manager", "admin", "super_admin"];

function monthStartISO(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
}

function todayISO(): string {
  return new Date().toISOString().split("T")[0];
}

function fmt(n: number): string {
  return n.toLocaleString("fr-FR");
}

function money(n: number): string {
  return `${fmt(n)} XAF`;
}

export default function ProfilePage() {
  usePageHeader({ title: "Mon profil" });

  const { user } = useAuth();
  const canSeeOwnActivity = user ? ROLES_WITH_SELF_ACTIVITY.includes(user.role) : false;
  const from = monthStartISO();
  const to = todayISO();

  const { data: detail, isLoading } = useEmployeeActivityDetail(
    canSeeOwnActivity ? user?.id : undefined,
    { from, to, basis: "accrual" },
  );

  if (!user) return null;

  return (
    <div className="max-w-5xl">
      <EmployeeProfileHeader
        employee={{ id: user.id, fullName: user.fullName, email: user.email, role: user.role }}
        subtitleLabel="Profil opérationnel"
      />

      {canSeeOwnActivity ? (
        detail ? (
          <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr] lg:items-start">
            <div>
              <p className="mb-2 text-[12px] font-semibold text-foreground">
                Aperçu de votre activité (ce mois)
              </p>
              <EmployeeProfileKpis
                maxColumns={2}
                items={[
                  {
                    label: "Factures émises",
                    value: money(detail.invoices.reduce((s, r) => s + r.amount, 0)),
                    detail: `${detail.invoices.length} document${detail.invoices.length > 1 ? "s" : ""}`,
                    icon: FileText,
                    tone: "success",
                  },
                  {
                    label: "Encaissements",
                    value: money(detail.payments.reduce((s, r) => s + r.amount, 0)),
                    detail: `${detail.payments.length} paiement${detail.payments.length > 1 ? "s" : ""}`,
                    icon: ReceiptText,
                    tone: "neutral",
                  },
                  {
                    label: "Mouvements épargne",
                    value: money(detail.savingsTransactions.reduce((s, r) => s + r.amount, 0)),
                    detail: `${detail.savingsTransactions.length} mouvement${detail.savingsTransactions.length > 1 ? "s" : ""}`,
                    icon: PiggyBank,
                    tone: "neutral",
                  },
                  {
                    label: "Actions suivies",
                    value: String(
                      detail.invoices.length +
                        detail.payments.length +
                        detail.commissions.length +
                        detail.stockMovements.length +
                        detail.savingsTransactions.length,
                    ),
                    detail: "Sur le mois en cours",
                    icon: BarChart3,
                    tone: "gold",
                  },
                ]}
              />
            </div>
            <EmployeeRecentActivity events={buildRecentEvents(detail)} />
          </div>
        ) : (
          !isLoading && null
        )
      ) : (
        <p className="text-[12px] text-muted-foreground">
          Le résumé d'activité personnalisé n'est pas encore disponible pour votre rôle.
        </p>
      )}
    </div>
  );
}
